import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { UserContext } from 'globalContext/UserContext';
import { accountFilledInfoUrl } from 'router/url';
import MissingUserProfileInfoDialog from 'components/Dialog/MissingInfoDialog';
import {
  MissingInfoType,
  MissingUserProfileInfo,
} from 'components/Dialog/MissingInfoDialog/types';
import FilterSummary from 'components/FilterSummary';
import {
  fetchClientList,
  fetchEmployeeList,
  fetchProjectList,
} from 'utils/commonFetches';
import { getFetch } from 'utils/fetchFunctions';
import { returnRoleInclusion } from 'utils/helpers';
import {
  ClientsData,
  FilterOption,
  ProjectsData,
} from 'utils/helpers/renameKeys';
import { useIndexIcon } from 'utils/hooks/useIndexIcon';
import { usePagination } from 'utils/hooks/usePagination';
import useQuery from 'utils/hooks/useQuery';
import { useToggleState } from 'utils/hooks/useToggleState';
import { RoleTypes } from 'utils/rolesTypes';
import { BusinessTripsManagerProvider } from './BusinessTripManagerContext/Context';
import { TripCalculatorProvider } from './Screens/BusinessTripCalculator/TripCalculatorContext';
import BusinessTripsTable from './Screens/BusinessTripsTable';
import BusinessTripBox from './BusinessTripBox';
import BusinessTripPickers from './BusinessTripPickers';
import BusinessTripDialog from './Dialog';
import {
  getDefaultTripFilters,
  getEffectiveStatuses,
  getNeedsActionStatuses,
  getTripAccess,
  getTripsUrl,
  normalizeTripFilters,
  readTripFiltersFromQuery,
  TripFilterState,
  writeTripFiltersToQuery,
} from './filters';
import { TRIPS_HEAD_DATA } from './static_data';
import { BusinessTrip, BusinessTripsFilters, TripStatuses } from './types';
import { projectsFilter } from './utils';
import { TripsBox } from './BusinessTrips.css';

const SELECT_FILTERS = ['client', 'project', 'employee'] as const;

const readSelectFromQuery = (query: URLSearchParams, name: string) => {
  const id = Number(query.get(`${name}Id`));
  const label = query.get(`${name}Name`);
  return id && label ? { value: id, name: label, clientName: label } : null;
};

const BusinessTrips = () => {
  const intl = useIntl();
  const query = useQuery();
  const history = useHistory();
  const location = useLocation();
  const [showAddTripDialog, toggleAddTripDialog] = useToggleState(false);
  const [tripsChanged, toggleTripsChanged] = useToggleState(false);
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<BusinessTrip[]>([]);
  const [employeesData, setEmployeesData] = useState<FilterOption[]>([]);
  const [clientsData, setClientsData] = useState<ClientsData[]>([]);
  const [projectsData, setProjectsData] = useState<ProjectsData[]>([]);
  const [openMissingInfoDialog, setOpenMissingInfoDialog] = useState(false);
  const [missingInfo, setMissingInfo] = useState<MissingUserProfileInfo>();
  const [needsCount, setNeedsCount] = useState<number>();
  const [, setPreviousParameters] = useState<string>();
  const syncedSearch = useRef<string>();
  const latestTripsRequest = useRef(0);
  const [showDescription, selectedId, renderIcon, resetIndex] =
    useIndexIcon(null);

  const { roles, username, teamLeader } = useContext(UserContext);
  const isAccountPrivileged = useMemo(
    () =>
      returnRoleInclusion(roles, [
        RoleTypes.RoleDEVHR,
        RoleTypes.RoleAccounting,
        RoleTypes.RoleAdministration,
      ]),
    [roles],
  );
  const access = useMemo(
    () => getTripAccess(roles, teamLeader),
    [roles, teamLeader],
  );
  const filterDefaults = useMemo(() => getDefaultTripFilters(access), [access]);

  const instantlyChangeTripState = useCallback(
    (id: number, state: TripStatuses) => {
      const updatedTableData: BusinessTrip[] = tableData.map((businessTrip) => {
        if (businessTrip.id === id) {
          return { ...businessTrip, status: state };
        }
        return businessTrip;
      });

      setTableData(updatedTableData);
    },
    [tableData],
  );
  const { control, getValues, setValue } = useForm<BusinessTripsFilters>({
    // Scope, preset and statuses have no input of their own; keep their
    // values in the form so setValue and useWatch work without registration.
    shouldUnregister: false,
    defaultValues: {
      employee: readSelectFromQuery(query, 'employee') ?? undefined,
      project: readSelectFromQuery(query, 'project') ?? undefined,
      client: readSelectFromQuery(query, 'client') ?? undefined,
      ...readTripFiltersFromQuery(query, filterDefaults, access),
    },
  });

  const formData: BusinessTripsFilters = useWatch({
    control,
  }) as BusinessTripsFilters;
  const { employee, project, client, scope, preset, statuses } = formData;
  const filters = useMemo<TripFilterState>(
    () => ({ scope, preset, statuses: statuses ?? [] }),
    [scope, preset, statuses],
  );
  const effectiveStatuses = useMemo(
    () => getEffectiveStatuses(filters, access),
    [filters, access],
  );

  const tripIdFromQuery = useMemo(() => query.get('id') ?? undefined, [query]);

  const {
    debouncedPage,
    pagination,
    setRowsPerPage,
    setCurrentPage,
    setElementsCount,
  } = usePagination<FormData>({
    initialPage: 0,
    initialRowsPerPage: 10,
    initialElementsCount: 10,
    debounceTime: 300,
  });

  const fetchTrips = useCallback(async () => {
    setLoading(true);
    if (!username) {
      return null;
    }

    const queryString = getTripsUrl(
      {
        pageNumber: debouncedPage,
        pageSize: pagination.pageSize,
        scope: filters.scope,
        statuses: effectiveStatuses,
        username,
        employeeId: filters.scope === 'mine' ? undefined : employee?.value,
        clientId: client?.value,
        projectId: project?.value,
        businessTripId: tripIdFromQuery,
      },
      access,
    );

    setPreviousParameters((prevParams) => {
      if (prevParams !== queryString) {
        resetIndex();
      }
      return queryString;
    });

    const requestNumber = ++latestTripsRequest.current;
    const tripsResponse = await getFetch({
      url: queryString,
    });
    // Filter, page and URL changes overlap; only the latest request may
    // update the table.
    if (requestNumber !== latestTripsRequest.current) {
      return;
    }

    setTableData(tripsResponse?.content);

    setElementsCount(tripsResponse?.totalElements);

    setLoading(false);
  }, [
    username,
    debouncedPage,
    pagination.pageSize,
    filters.scope,
    effectiveStatuses,
    employee?.value,
    client?.value,
    project?.value,
    setElementsCount,
    resetIndex,
    setPreviousParameters,
    tripIdFromQuery,
    access,
  ]);

  const fetchNeedsCount = useCallback(async () => {
    if (!username) {
      return;
    }
    const response = await getFetch({
      url: getTripsUrl(
        {
          pageNumber: 0,
          pageSize: 1,
          scope: filters.scope,
          statuses: getNeedsActionStatuses(filters.scope, access),
          username,
        },
        access,
      ),
    });
    setNeedsCount(
      typeof response?.totalElements === 'number'
        ? response.totalElements
        : undefined,
    );
  }, [username, filters.scope, access]);

  useEffect(() => {
    fetchTrips();
  }, [fetchTrips, tripsChanged]);

  useEffect(() => {
    fetchNeedsCount();
  }, [fetchNeedsCount, tripsChanged]);

  useEffect(() => {
    fetchEmployeeList(setEmployeesData);
    fetchClientList(setClientsData);
    fetchProjectList(setProjectsData);
  }, []);

  const writeFiltersToUrl = useCallback(
    (
      tripFilters: TripFilterState,
      selected: Pick<BusinessTripsFilters, 'client' | 'project' | 'employee'>,
      removeLinkedTrip = false,
    ) => {
      const params = writeTripFiltersToQuery(
        new URLSearchParams(history.location.search),
        tripFilters,
        filterDefaults,
      );
      if (removeLinkedTrip) {
        params.delete('id');
      }
      SELECT_FILTERS.forEach((name) => {
        const value =
          name === 'employee' && tripFilters.scope === 'mine'
            ? null
            : selected[name];
        params.delete(`${name}Id`);
        params.delete(`${name}Name`);
        if (value?.value && value?.name) {
          params.set(`${name}Id`, String(value.value));
          params.set(`${name}Name`, value.name);
        }
      });
      const search = params.toString();
      syncedSearch.current = search;
      if (search !== history.location.search.replace(/^\?/, '')) {
        history.replace({ pathname: history.location.pathname, search });
      }
    },
    [filterDefaults, history],
  );

  useEffect(() => {
    writeFiltersToUrl(filters, { client, project, employee });
  }, [filters, client, project, employee, writeFiltersToUrl]);

  const onFilterChange = useCallback(
    (changes: Partial<TripFilterState>) => {
      const { scope, preset, statuses } = getValues();
      // Normalized as the URL reader does, so a reload shows the same filters.
      const next = normalizeTripFilters(
        { scope, preset, statuses: statuses ?? [], ...changes },
        access,
      );
      (Object.keys(next) as (keyof TripFilterState)[]).forEach((key) =>
        setValue(key, next[key]),
      );
      if (changes.scope === 'mine') {
        setValue('employee', null);
      }
    },
    [access, getValues, setValue],
  );

  // Navigation to this page with other filters (Back, Forward, or a link
  // followed while it is open) updates the form. The page's own URL writes
  // are recorded in syncedSearch and skipped.
  useEffect(() => {
    const search = location.search.replace(/^\?/, '');
    if (syncedSearch.current === undefined) {
      syncedSearch.current = search;
      return;
    }
    if (search === syncedSearch.current) {
      return;
    }
    syncedSearch.current = search;
    const params = new URLSearchParams(search);
    onFilterChange(readTripFiltersFromQuery(params, filterDefaults, access));
    SELECT_FILTERS.forEach((name) =>
      setValue(name, readSelectFromQuery(params, name)),
    );
  }, [access, filterDefaults, location.search, onFilterChange, setValue]);

  const resetFilters = useCallback(() => {
    writeFiltersToUrl(
      filterDefaults,
      { client: undefined, project: undefined, employee: undefined },
      true,
    );
    onFilterChange(filterDefaults);
    SELECT_FILTERS.forEach((name) => setValue(name, null));
  }, [filterDefaults, onFilterChange, setValue, writeFiltersToUrl]);

  const changedFilterCount = [
    filters.scope !== filterDefaults.scope,
    filters.preset !== filterDefaults.preset,
    !!client?.value,
    !!project?.value,
    filters.scope !== 'mine' && !!employee?.value,
  ].filter(Boolean).length;
  const summaryParts = [
    ...(tripIdFromQuery
      ? [
          intl.formatMessage(
            { id: 'BUSINESS_TRIP.FILTER.LINKED_TRIP' },
            { id: tripIdFromQuery },
          ),
        ]
      : []),
    ...(changedFilterCount
      ? [
          intl.formatMessage(
            { id: 'BUSINESS_TRIP.FILTER.CHANGED' },
            { count: changedFilterCount },
          ),
        ]
      : []),
  ];

  const projectsOptions = () => {
    if (client) {
      return projectsFilter(client.name, projectsData);
    }
    return projectsData;
  };

  const getAccountFilledInfo = useCallback(async () => {
    const response: MissingUserProfileInfo = await getFetch({
      url: accountFilledInfoUrl,
    });
    setMissingInfo(response);
    if (response && !response.personalIdDefined) {
      setOpenMissingInfoDialog(true);
    }
  }, [setOpenMissingInfoDialog]);

  useEffect(() => {
    getAccountFilledInfo();
  }, [getAccountFilledInfo]);

  const handleAddBusinessTrip = useCallback(async () => {
    await getAccountFilledInfo();
    if (missingInfo?.personalIdDefined) {
      toggleAddTripDialog();
    }
  }, [getAccountFilledInfo, missingInfo, toggleAddTripDialog]);

  useEffect(() => {
    setCurrentPage(null, 0);
  }, [formData, setCurrentPage]);

  return (
    <>
      <BusinessTripsManagerProvider>
        <TripsBox>
          <TripCalculatorProvider>
            <BusinessTripBox
              onAddBusinessTrip={handleAddBusinessTrip}
              roles={roles}
              filterBar={{
                access,
                filters,
                needsCount,
                loading,
                onChange: onFilterChange,
              }}
            />
          </TripCalculatorProvider>
          <BusinessTripsTable
            fetchTrips={fetchTrips}
            data={tableData}
            setCurrentPage={setCurrentPage}
            setRowsPerPage={setRowsPerPage}
            pageCount={pagination.totalElements}
            currentPage={pagination.pageNumber}
            withPagination
            limit={pagination.pageSize}
            head={TRIPS_HEAD_DATA}
            loading={loading}
            isAccountPrivileged={isAccountPrivileged}
            isLeader={teamLeader}
            withNameColumn={filters.scope !== 'mine'}
            instantlyChangeTripState={instantlyChangeTripState}
            tripsChanged={toggleTripsChanged}
            showDescription={showDescription}
            selectedId={selectedId}
            renderIcon={renderIcon}
            paginationStart={
              <>
                <BusinessTripPickers
                  control={control}
                  clients={clientsData}
                  projects={projectsOptions()}
                  employees={employeesData}
                  setValue={setValue}
                  showEmployeeFilter={filters.scope !== 'mine'}
                  loading={loading}
                />
                <FilterSummary
                  parts={summaryParts}
                  resetLabel={intl.formatMessage({
                    id: 'BUSINESS_TRIP.FILTER.RESET',
                  })}
                  onReset={resetFilters}
                />
              </>
            }
          />
        </TripsBox>

        <BusinessTripDialog
          addChecked={showAddTripDialog}
          handleAddChanged={toggleAddTripDialog}
          tripsChanged={toggleTripsChanged}
        />

        {openMissingInfoDialog && (
          <MissingUserProfileInfoDialog
            onClose={setOpenMissingInfoDialog}
            missingInfo={missingInfo}
            type={MissingInfoType.businessTrip}
          />
        )}
      </BusinessTripsManagerProvider>
    </>
  );
};

export default BusinessTrips;
